import React, { useState } from "react";
import { Info, CaretDown } from "@phosphor-icons/react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid } from "recharts";

export const fmtDuration = (sec) => {
  if (sec == null) return null;
  if (Math.round(sec * 10) / 10 < 60) return `${Math.round(sec * 10) / 10} s`;
  if (Math.round(sec / 6) / 10 < 60) return `${Math.round(sec / 6) / 10} min`;
  const mins = Math.round(sec / 60);
  return `${Math.floor(mins / 60)} h ${String(mins % 60).padStart(2, "0")} min`;
};

export const REASON_OPTIONS = [
  ["", "Todos los motivos"], ["ventas", "Ventas / cotización de vehículo"], ["test_drive", "Test drive"],
  ["cita_mantenimiento", "Cita · Mantenimiento"], ["cita_mant_chequeo", "Cita · Mantenimiento y Chequeo"],
  ["cita_revision", "Cita · Revisión (Chequeo/Reparación)"], ["recall", "Recall o Campaña de Seguridad"],
  ["cita_otro", "Cita de taller · otro motivo"], ["piezas", "Piezas y accesorios"], ["segundo_propietario", "Segundo propietario"],
  ["financiamiento", "Financiamiento"], ["informacion", "Información general"],
];

export const STATUS_OPTIONS = [
  ["", "Todos los estados"], ["open", "Abierta"], ["assigned", "Asignada"], ["escalated", "Escalada"],
  ["resolved", "Resuelta"], ["closed", "Cerrada"], ["abandoned", "Abandonada"],
];

// [what it measures, how it is calculated, unit]
export const KPI_INFO = {
  "kpi-total-conv": ["Conversaciones iniciadas en el rango y filtros.", "Conteo de conversaciones por fecha de inicio. El % compara con el período anterior.", "Conversaciones"],
  "kpi-attended": ["Conversaciones atendidas con un cierre o traspaso.", "Resueltas + cerradas + escaladas.", "Conversaciones"],
  "kpi-abandoned": ["Clientes que dejaron de responder.", "12 h sin respuesta del cliente después de un mensaje del bot o del agente (incluye las que luego se cerraron solas a las 24 h).", "Conversaciones y % del total"],
  "kpi-abandoned-bot": ["Abandono antes de pasar a un agente.", "Abandonadas que nunca se traspasaron a un humano ÷ total de conversaciones.", "Conversaciones y %"],
  "kpi-abandoned-agent": ["Abandono durante la atención humana.", "Abandonadas que ya estaban asignadas o escaladas a un agente ÷ total de conversaciones.", "Conversaciones y %"],
  "kpi-closed": ["Conversaciones cerradas definitivamente.", "Estado Cerrada (se cierran solas 7 días después de resueltas).", "Conversaciones"],
  "kpi-escalated": ["Conversaciones esperando a un agente.", "Estado Escalada en este momento.", "Conversaciones"],
  "kpi-open": ["Conversaciones activas.", "Estado Abierta en este momento.", "Conversaciones"],
  "kpi-bot-time": ["Rapidez de la primera respuesta del bot.", "Promedio de: primer mensaje del cliente → primera respuesta del bot. Se excluyen esperas > 10 min (bot apagado o en pausa).", "Segundos / minutos"],
  "kpi-human-frt": ["Rapidez de la primera respuesta del agente.", "Promedio de: traspaso al agente → su primer mensaje (máx. 24 h).", "Segundos / minutos"],
  "kpi-bot-aht": ["Duración de las conversaciones atendidas solo por el bot.", "Promedio de primer a último mensaje en conversaciones sin traspaso.", "Segundos / minutos"],
  "kpi-aht": ["Tiempo de manejo del agente.", "Promedio del tiempo registrado por el agente en sus casos.", "Minutos"],
  "kpi-resolution": ["Duración real de la atención completa.", "Promedio de: primer mensaje → resolución, en conversaciones resueltas o cerradas (excluye cierres automáticos por inactividad y > 7 días).", "Minutos / horas"],
  "kpi-ia-resolution": ["Casos resueltos sin agente.", "Cerradas sin traspaso ÷ (cerradas + escaladas).", "%"],
  "kpi-human-resolution": ["Casos que necesitaron un agente.", "Escaladas ÷ (cerradas + escaladas).", "%"],
  "kpi-csat": ["Satisfacción del cliente.", "Promedio de las encuestas (1 a 5) de las conversaciones del rango y filtros.", "Estrellas (1–5)"],
};

export const InfoTip = ({ id }) => {
  const info = KPI_INFO[id];
  if (!info) return null;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" className="text-muted-foreground hover:text-foreground" data-testid={`${id}-info`} aria-label="Cómo se calcula">
            <Info size={13} />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs space-y-1">
          <div><b>Qué mide:</b> {info[0]}</div>
          <div><b>Cálculo:</b> {info[1]}</div>
          <div><b>Unidad:</b> {info[2]}</div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const ContactReasons = ({ rows }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="contact-reasons">
    <div className="rounded-lg border border-border bg-card p-4">
      <ResponsiveContainer width="100%" height={Math.max(180, rows.length * 30)}>
        <BarChart data={rows} layout="vertical" margin={{ left: 10, right: 20 }}>
          <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} horizontal={false} />
          <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} />
          <YAxis type="category" dataKey="label" width={190} tick={{ fontSize: 10 }} />
          <RTooltip formatter={(v, _n, p) => [`${v} (${p.payload.pct}%)`, "Conversaciones"]} />
          <Bar dataKey="count" fill="#0052CC" radius={[0, 4, 4, 0]} maxBarSize={22} />
        </BarChart>
      </ResponsiveContainer>
    </div>
    <div className="rounded-lg border border-border bg-card overflow-x-auto">
      <table className="w-full text-xs" data-testid="contact-reasons-table">
        <thead className="text-[10px] uppercase tracking-wider text-muted-foreground">
          <tr className="border-b border-border"><th className="text-left p-2.5">Motivo</th><th className="text-right p-2.5">Volumen</th>
            <th className="text-right p-2.5">%</th><th className="text-right p-2.5">Completados</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-b border-border/60" data-testid={`reason-row-${r.key}`} title={`Se registra: ${r.created_by}. Completado: ${r.completed_when}.`}>
              <td className="p-2.5 font-medium">{r.label}</td><td className="p-2.5 text-right font-mono">{r.count}</td>
              <td className="p-2.5 text-right font-mono">{r.pct}%</td><td className="p-2.5 text-right font-mono">{r.completed}</td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={4} className="p-4 text-center text-muted-foreground">Sin conversaciones en el rango</td></tr>}
        </tbody>
      </table>
    </div>
  </div>
);

const RULES = [
  "Cada conversación cuenta una sola vez y en un solo motivo, por prioridad: Segundo propietario → Ventas/Test drive (lead) → Cita de taller (según su motivo) → Piezas → Financiamiento → Información general. Así no hay duplicados.",
  "Segundo propietario: el caso se registra cuando el bot recibe matrícula, cédula (ambos lados), dirección y teléfono, y se asigna como pendiente a un representante. Se completa cuando el agente resuelve la conversación. Cuenta solo para el agente asignado.",
  "Ventas y Test drive: se registran al crear el lead en Dynamics; se completan cuando Zapier confirma el envío.",
  "Citas de taller: se registran con la solicitud de cita del bot (Mantenimiento, Mantenimiento y Chequeo, Revisión = Chequeo/Reparación, Recall o Campaña). Se completan al resolver la conversación.",
  "Piezas: se registran al escalar la cotización; se completan cuando el agente marca Cotización enviada.",
  "Reasignación: si el agente asignado no responde, la conversación pasa al siguiente agente conectado del equipo: por defecto a los 3 minutos en un traspaso nuevo y a los 10 minutos si un agente ya la atendió o fue asignada a mano (ajustable en Configuración → Equipos). Tras 2 reasignaciones seguidas sin respuesta se avisa a los supervisores. Es la misma conversación: no se duplica ni se pierde el historial, y en el rendimiento cuenta solo para el agente asignado actual.",
  "Todos los indicadores respetan los filtros de fecha (inicio de la conversación), canal, motivo, agente y estado.",
];

export const KpiRules = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-border bg-card mt-6" data-testid="kpi-rules">
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-2 p-3 text-sm font-semibold" data-testid="kpi-rules-toggle">
        Reglas de negocio y fórmulas de los indicadores
        <CaretDown size={13} className={`ml-auto transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 text-xs space-y-3">
          <table className="w-full">
            <tbody>
              {Object.entries(KPI_INFO).map(([k, [what, how, unit]]) => (
                <tr key={k} className="border-b border-border/50 align-top">
                  <td className="py-1.5 pr-3 text-muted-foreground w-1/4">{what}</td><td className="py-1.5 pr-3">{how}</td><td className="py-1.5 w-28">{unit}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <ul className="list-disc pl-4 space-y-1.5">{RULES.map((r) => <li key={r}>{r}</li>)}</ul>
        </div>
      )}
    </div>
  );
};
